import React, { useMemo } from 'react';
import { Column } from '@ant-design/plots';
import { Typography } from 'antd';
import { useTheme } from '../../theme/ThemeProvider';
import {
  aggregateCountsByGroup,
  getRequestGroupKey,
  getRequestGroupLabel,
  formatGroupWithSources,
} from '../../utils/requestTypeLabels';
import {
  buildPeriodBuckets,
  bucketKeyForTs,
  PERIOD_CHART_HINTS,
} from '../../utils/chartPeriodFormat';

const { Text } = Typography;

function buildChartRows(rawSeries, dateFrom, dateTo, period) {
  const grouped = {};
  const groupKeys = new Set();

  for (const row of rawSeries || []) {
    const groupKey = getRequestGroupKey(row.type);
    groupKeys.add(groupKey);
    const key = bucketKeyForTs(row.ts, period);
    if (!grouped[key]) grouped[key] = {};
    grouped[key][groupKey] = (grouped[key][groupKey] || 0) + Number(row.count || 0);
  }

  const groups = [...groupKeys];
  const buckets = buildPeriodBuckets(dateFrom, dateTo, period);
  const rows = [];

  for (const bucket of buckets) {
    const key = bucketKeyForTs(bucket.ts, period);
    const breakdown = grouped[key] || {};
    for (const groupKey of groups) {
      rows.push({
        time: bucket.label,
        sortKey: bucket.sortKey,
        group: getRequestGroupLabel(groupKey),
        count: breakdown[groupKey] || 0,
      });
    }
  }

  return rows.sort((a, b) => a.sortKey - b.sortKey || a.group.localeCompare(b.group, 'ru'));
}

const WorkRequestsTimeline = ({
  series = [],
  period = 'day',
  dateFrom,
  dateTo,
  loading,
}) => {
  const { darkMode } = useTheme();

  const chartData = useMemo(
    () => buildChartRows(series, dateFrom, dateTo, period),
    [series, dateFrom, dateTo, period]
  );

  const summary = useMemo(() => {
    const byGroup = aggregateCountsByGroup(
      (series || []).map((r) => ({ type: r.type, count: r.count })),
      'type',
      'count'
    );
    return Object.entries(byGroup)
      .map(([key, count]) => ({
        key,
        label: getRequestGroupLabel(key),
        count,
        hint: formatGroupWithSources(key),
      }))
      .sort((a, b) => b.count - a.count);
  }, [series]);

  const hasRequests = (series || []).some((r) => Number(r.count) > 0);

  if (loading) {
    return <div className="admin-requests-chart admin-requests-chart--loading">Загрузка…</div>;
  }

  if (!hasRequests) {
    return <Text type="secondary">Нет запросов за выбранный период</Text>;
  }

  return (
    <div className="admin-requests-chart">
      <Text type="secondary" className="admin-requests-chart__hint">
        {PERIOD_CHART_HINTS[period] || PERIOD_CHART_HINTS.day}
      </Text>

      {summary.length > 0 && (
        <div className="admin-requests-chart__summary">
          {summary.map((s) => (
            <span key={s.key} className="admin-requests-chart__tag" title={s.hint}>
              {s.label}: <strong>{s.count}</strong>
            </span>
          ))}
        </div>
      )}

      {chartData.length > 0 ? (
        <Column
          data={chartData}
          xField="time"
          yField="count"
          colorField="group"
          stack
          height={280}
          legend={{ position: 'bottom' }}
          theme={darkMode ? 'dark' : 'light'}
        />
      ) : (
        <Text type="secondary">Недостаточно данных для графика</Text>
      )}
    </div>
  );
};

export default WorkRequestsTimeline;
